import React, { useState } from "react";
import { BigNumber } from "@ethersproject/bignumber";
import BN from "bignumber.js";
import axios from "axios";
import {
  Box,
  Text,
  Select,
  Divider,
  Table,
  TableCaption,
  Thead,
  Tbody,
  Th,
  Tr,
  Td,
  Spinner,
  Grid,
  GridItem,
  Input,
} from "@chakra-ui/react";

import Card from "components/Card/Card";
import StackAreaChart from "components/StackAreaChart/StackAreaChart";
import PriceAndVolatility from "components/PriceAndVolatilityChart/PriceAndVolatilityChart";

import { BEACH_READY_SCORE } from "utils/beachReady";

const getPercentageDiff = (address, tokensHoldersBalance, daysTimestamps) => {
  const lastDayTs = daysTimestamps[0];
  const firstDayTs = daysTimestamps[daysTimestamps.length - 1];
  const firstDayBalance = tokensHoldersBalance[firstDayTs][address];
  const lastDayBalance = tokensHoldersBalance[lastDayTs][address];

  if (firstDayBalance && lastDayBalance) {
    const diviser = new BN(firstDayBalance.sub(lastDayBalance).toString());
    const denominator = new BN(lastDayBalance.toString());
    const result = diviser
      .dividedBy(denominator)
      .multipliedBy(100)
      .toFixed(2)
      .toString();
    return result + "%";
  } else if (!firstDayBalance && lastDayBalance) {
    return "-100%";
  } else if (firstDayBalance && !lastDayBalance) {
    return "100%";
  } else {
    return "0";
  }
};

const GeneralInfo = ({ config }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedToken, setSelectedToken] = useState("");
  const [tokensHoldersBalance, setTokensHoldersBalance] = useState({});
  const [daysTimestamps, setDaysTimestamps] = useState([]);
  const [first35Holder, setFirst35Holder] = useState([]);
  const [dataToChart, setDataToChart] = useState([]);
  const [priceAndVolatility, setPriceAndVolatility] = useState([]);
  const [volatility, setVolatitly] = useState({});

  const _t = new Date();
  const [date, setDate] = useState(_t.toISOString().slice(0, 10));

  const [volatilityLastEpoch, setVolatitlyLastEpoch] = useState({});
  const today = daysTimestamps.length
    ? daysTimestamps[daysTimestamps.length - 1]
    : "";

  const onDateChange = async (e) => {
    setDate(e.target.value);
    await fetchVolatility(selectedToken, e.target.value);
  };

  const fetchVolatility = async (_address, _date) => {
    try {
      if (!_address) return;
      const {
        data: { data: volatilityData, code: volatilityCode },
      } = await axios.post(
        `${config.bws.pythonUrl}/token/volatility-on-epoch`,
        {
          token_address: _address,
          date: _date,
        },
      );

      if (volatilityCode === 200) {
        setVolatitlyLastEpoch(volatilityData);
      } else {
        setVolatitlyLastEpoch({});
      }
    } catch (e) {
      console.log(e);
      setVolatitlyLastEpoch({});
    }
  };

  const fetchHolders = async (_selectedToken) => {
    setIsLoading(true);
    setTokensHoldersBalance({});
    setFirst35Holder([]);
    setDataToChart([]);
    setPriceAndVolatility([]);

    await fetchVolatility(_selectedToken, date);

    try {
      const {
        data: {
          data: { data: priceData, volatility: volatilityData },
          code: priceCode,
        },
      } = await axios.post(`${config.bws.pythonUrl}/token/rolling-volatility`, {
        token_address: _selectedToken,
      });

      if (priceCode === 200) {
        setPriceAndVolatility(priceData);
        setVolatitly(volatilityData);
      }
    } catch (e) {
      console.log(e);
    }

    try {
      const { data } = await axios.get(
        `${config.bws.pythonUrl}/holders/${_selectedToken}`,
      );

      if (data.code === 200) {
        const _tokensHoldersBalance = {};
        const _daysTimestamps = [];

        const { data: days } = data;
        const holdersMap = new Map();

        Object.keys(days).forEach((day) => {
          _daysTimestamps.push(day);

          const currentDay = days[day];

          days[day].map(({ address, balance, ts }) => {
            const balanceBN = BigNumber.from(balance);

            const toAdd = {
              ts: day,
              balance: balanceBN,
            };

            if (!_tokensHoldersBalance[day]) _tokensHoldersBalance[day] = {};
            _tokensHoldersBalance[day][address] = balanceBN;

            if (holdersMap.has(address)) {
              holdersMap.set(address, holdersMap.get(address).add(balanceBN));
            } else {
              holdersMap.set(address, balanceBN);
            }
          });
        });

        const unsortedHolders = [...holdersMap];
        const sortedHolders = unsortedHolders.sort((a, b) => {
          const aBalance = a[1]; // [address, balance]
          const bBalance = b[1];
          return aBalance.gte(bBalance) ? -1 : 1;
        });
        const topHolders = sortedHolders
          .slice(0, 35)
          .map(([address]) => address);
        const dayone = _daysTimestamps[0];
        const holderone = topHolders[0];
        const numberOneHoldersDigitLength =
          _tokensHoldersBalance[dayone][holderone].toString().length - 5;
        const bnPower = new BN(10).pow(numberOneHoldersDigitLength);

        const _dataToChart = _daysTimestamps.map((_day) => {
          const holdersOfTheDay = {};

          topHolders.map((address) => {
            const bn = _tokensHoldersBalance[_day][address]
              ? BN(_tokensHoldersBalance[_day][address].toString())
              : BN(0);

            holdersOfTheDay[address] = parseFloat(
              bn.toString(),
              // bn.dividedBy(bnPower).toString(),
            );
          });

          return {
            date: _day,
            ...holdersOfTheDay,
          };
        });

        setFirst35Holder(topHolders);
        setDaysTimestamps(_daysTimestamps);
        setTokensHoldersBalance(_tokensHoldersBalance);
        setDataToChart(_dataToChart);
      }
    } catch (e) {
      console.log(e);
    }

    setIsLoading(false);
  };

  return (
    <Box display="flex" width="100%" maxWidth="80vw" flexDir="column" py={10}>
      <Select
        placeholder="Select Token"
        size="lg"
        color="white"
        border="2px solid rgb(140, 140, 227)"
        onChange={(e) => {
          setSelectedToken(e.target.value);
          fetchHolders(e.target.value);
        }}
      >
        <option value="0x50b7545627a5162f82a992c33b87adc75187b218">
          WBTC.e (0x50b7545627a5162f82a992c33b87adc75187b218)
        </option>
        <option value="0x49d5c2bdffac6ce2bfdb6640f4f80f226bc10bab">
          WETH.e (0x49d5c2bdffac6ce2bfdb6640f4f80f226bc10bab)
        </option>
        <option value="0xB31f66AA3C1e785363F0875A1B74E27b85FD66c7">
          WAVAX (0xB31f66AA3C1e785363F0875A1B74E27b85FD66c7)
        </option>
        <option value="0x5947bb275c521040051d82396192181b413227a3">
          LINK.e (0x5947bb275c521040051d82396192181b413227a3)
        </option>
        <option value="0xA32608e873F9DdEF944B24798db69d80Bbb4d1ed">
          CRA (0xA32608e873F9DdEF944B24798db69d80Bbb4d1ed)
        </option>
        <option value="0x6e84a6216ea6dacc71ee8e6b0a5b7322eebc0fdd">
          JOE (0x6e84a6216ea6dacc71ee8e6b0a5b7322eebc0fdd)
        </option>
        <option value="0x22d4002028f537599bE9f666d1c4Fa138522f9c8">
          PTP (0x22d4002028f537599bE9f666d1c4Fa138522f9c8)
        </option>
      </Select>

      <Divider my={5} />

      <Box display="flex" gap="10px" width="100%">
        <Card display="flex">
          <Text color="white" strong="true">
            Price and Volatility
          </Text>
          <Divider my={3} />
          {isLoading ? (
            <Box display="flex" justifyContent="center" width="100%">
              <Spinner
                thickness="4px"
                speed="0.65s"
                emptyColor="gray.200"
                color="blue.500"
                size="xl"
              />
            </Box>
          ) : (
            priceAndVolatility.length && (
              <PriceAndVolatility data={priceAndVolatility} />
            )
          )}
        </Card>
        <Card display="flex">
          <Text color="white" strong="true">
            Volatility Info
          </Text>
          {isLoading ? (
            <Box display="flex" justifyContent="center" width="100%">
              <Spinner
                thickness="4px"
                speed="0.65s"
                emptyColor="gray.200"
                color="blue.500"
                size="xl"
              />
            </Box>
          ) : (
            <Table variant="simple" overflow="scroll">
              <Tbody>
                <Tr>
                  <Td color="white">Rolling Volatility Today</Td>
                  <Td isNumeric color="rgb(140, 140, 227)">
                    {volatility.today}
                  </Td>
                </Tr>

                <Tr>
                  <Td color="white">Rolling Volatility 7 days ago</Td>
                  <Td isNumeric color="rgb(140, 140, 227)">
                    {volatility.seven_days_ago}
                  </Td>
                </Tr>

                <Tr>
                  <Td color="white">Rolling Volatility 30 days ago</Td>
                  <Td isNumeric color="rgb(140, 140, 227)">
                    {volatility.thirday_days_ago}
                  </Td>
                </Tr>

                <Tr>
                  <Td color="white">Log Pct Change from 7 days ago</Td>
                  <Td isNumeric color="rgb(140, 140, 227)">
                    {(volatility.log_change_seven_days_ago * 100).toFixed(2)}%
                  </Td>
                </Tr>

                <Tr>
                  <Td color="white">Log Pct Change from 30 days ago</Td>
                  <Td isNumeric color="rgb(140, 140, 227)">
                    {(volatility.log_change_thirty_days_ago * 100).toFixed(2)}%
                  </Td>
                </Tr>
              </Tbody>
            </Table>
          )}
          <Text color="white" strong="true" mt={8}>
            Beach Ready Score
          </Text>
          <Box>
            <Text
              textAlign="center"
              fontSize="3rem"
              color="rgb(140, 140, 227)"
              fontWeight="700"
            >
              {BEACH_READY_SCORE[selectedToken]
                ? BEACH_READY_SCORE[selectedToken]
                : "NA"}
            </Text>
            <Text textAlign="center" color="white" fontWeight="700">
              Risk adjusted proprietary metric provided by QV LABS
            </Text>
          </Box>
          <Text color="white" strong="true" mt={8} mb={3}>
            Epoch Volatility Lookup
          </Text>

          <Text
            textAlign="center"
            fontSize="3rem"
            color="rgb(140, 140, 227)"
            fontWeight="700"
          >
            {volatilityLastEpoch.volatilty
              ? `${(volatilityLastEpoch.volatilty * 100).toFixed(2)}%`
              : "NA"}
          </Text>
          <Text textAlign="center" color="white" fontWeight="700" my={3}>
            {volatilityLastEpoch.start_date
              ? `${volatilityLastEpoch.start_date} - ${volatilityLastEpoch.end_date}`
              : ""}
          </Text>
          <Input
            type="date"
            id="start"
            name="trip-start"
            value={date}
            background="white"
            min="2021-08-17"
            max={Date.now()}
            onChange={onDateChange}
          />
        </Card>
      </Box>

      <Divider my={5} />

      <Grid templateColumns="repeat(2, 1fr)" gap="10px">
        <GridItem>
          <Card display="flex">
            <Text color="white" strong="true">
              Historical Top Holders Activity
            </Text>
            <Divider my={3} />
            {dataToChart.length && (
              <StackAreaChart holders={first35Holder} data={dataToChart} />
            )}
          </Card>
        </GridItem>
        <GridItem>
          <Card display="flex">
            <Text color="white" strong="true">
              Historical Top Holders
            </Text>
            {isLoading ? (
              <Box display="flex" justifyContent="center" width="100%">
                <Spinner
                  thickness="4px"
                  speed="0.65s"
                  emptyColor="gray.200"
                  color="blue.500"
                  size="xl"
                />
              </Box>
            ) : (
              <Table variant="simple" overflow="scroll">
                <TableCaption>{`Top 35 holders: ${today}`}</TableCaption>
                <Thead>
                  <Tr>
                    <Th color="white">Address</Th>
                    <Th isNumeric color="white">
                      Balance
                    </Th>
                    <Th isNumeric color="white">
                      7 Days Diff
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {first35Holder &&
                    first35Holder.map((address) => (
                      <Tr key={`${address}_holder`}>
                        <Td color="white">{address}</Td>
                        <Td isNumeric color="rgb(140, 140, 227)">
                          {/* {Object.keys(tokensHoldersBalance)
                        .map((_d) =>
                          tokensHoldersBalance[_d][address]
                            ? tokensHoldersBalance[_d][address].toString()
                            : "0",
                        )
                        .join(", ")} */}
                          {tokensHoldersBalance[today][address]
                            ? tokensHoldersBalance[today][address].toString()
                            : "0"}
                        </Td>
                        <Td isNumeric color="rgb(140, 140, 227)">
                          {getPercentageDiff(
                            address,
                            tokensHoldersBalance,
                            daysTimestamps,
                          )}
                        </Td>
                      </Tr>
                    ))}
                </Tbody>
              </Table>
            )}
          </Card>
        </GridItem>
      </Grid>
    </Box>
  );
};

export default GeneralInfo;
